import React, {PureComponent} from 'react';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import Form from '../../components/Form';
import SideModal from '../../components/SideModal';
import Button from '../../components/Button';
import Input from '../../components/Input';
import ReportParameterInput from '../../components/ReportParameterInput';
import DateTimeInput from '../../components/DateTimeInput';
import ChipInput from '../../components/ChipInput';

class ScheduledReportFormModal extends PureComponent {
  state = this.getInitialState(this.props.report);

  getInitialState(report) {
    const schedule = this.props.schedule || createSchedule(report);
    // Set this value for those schedules that existed before selecting formats was available.
    schedule.format = (schedule.format || 'PDF').toUpperCase();

    return {
      saving: false,
      validationsEnabled: false,
      customTimeInterval: hasTimeInterval(report) && ['7', '30', '90', '365'].indexOf(schedule.parameters.timeInterval + '') < 0,
      report: report,
      id: null,
      ...schedule
    };
  }

  componentDidUpdate(prevProps) {
    const {schedule, report} = this.props;

    if (prevProps.schedule !== schedule || prevProps.report !== report) {
      this.setState(this.getInitialState(report));
    }
  }

  handleChangeTimeInterval = e => {
    const timeInterval = e.target.value;

    if (timeInterval === 'CUSTOM') {
      return this.setState(state => ({
        customTimeInterval: true,
        parameters: {
          ...state.parameters,
          timeInterval: state.parameters.timeInterval || '1'
        }
      }));
    }

    this.setState(state => ({
      customTimeInterval: false,
      parameters: {
        ...state.parameters,
        timeInterval
      }
    }));
  }

  handleChangeCustomTimeInterval = e => {
    const timeInterval = e.target.value;

    this.setState(state => ({
      parameters: {
        ...state.parameters,
        timeInterval
      }
    }));
  }

  handleSave = async () => {
    const {onSave, credentials} = this.props;
    let {saving, validationsEnabled, customTimeInterval, report, ...schedule} = this.state;
    const validations = getValidationErrors(report, schedule);

    if (hasValidationErrors(validations)) {
      return this.setState({validationsEnabled: true});
    }

    this.setState({saving: true});

    try {
      if (schedule.id) {
        schedule = await api.editScheduleReport(report.id, schedule.id, schedule, credentials);
        NotificationManager.success('Your schedule was updated');
      }
      else {
        schedule = await api.createScheduleReport(report.id, schedule, credentials);
        NotificationManager.success('Your report has been scheduled!');
      }

      this.setState({saving: false}, onSave(report, schedule));
    }
    catch (e) {
      NotificationManager.error('There was a problem while saving your report schedule. Please try again.');
      this.setState({saving: false});
    }
  }
  
  render() {
    const {reports, users, catalog, open, onClose} = this.props;
    const {saving, validationsEnabled, customTimeInterval, report, ...schedule} = this.state;
    const parameters = getRelevantParameters(report);
    const timeInterval = hasTimeInterval(report);
    const validations = getValidationErrors(report, schedule, {disabled: !validationsEnabled});
    const usersById = users.reduce((map, user) => {
      map[user.id] = user;
      return map;
    }, {});

    return (
      <Form onSubmit={this.handleSave}>
        <SideModal
          open={open}
          onClose={onClose}
          title={schedule.id ? 'Edit Schedule' : 'Schedule a Report'}
          options={[
            <Button type="submit" disabled={saving || hasValidationErrors(validations)}>
              {saving ? 'Saving...' : 'Save Schedule'}
            </Button>,
            <Button secondary onClick={onClose}>Cancel</Button>
          ]}>
          {open && (
            <SideModal.Content>
              {!schedule.id && (
                <Form.Group>
                  <Input
                    type="select"
                    id="scheduled-report-selector"
                    label="Report"
                    value={report && report.id}
                    onChange={e => this.setState(this.getInitialState(reports.filter(report => report.id === e.target.value)[0]))}
                    invalid={validations.report}
                    validationText={validations.report}>
                    <option value="">Choose a report to schedule</option>
                    {reports.map(report => (
                      <option key={report.id} value={report.id}>{report.name}</option>
                    ))}
                  </Input>
                </Form.Group>
              )}
              <Form.Group>
                <DateTimeInput
                  id="scheduled-report-schedule-time"
                  label="Schedule start date"
                  placeholder="Choose a date..."
                  helpText="When do you want to receive the first copy of this report?"
                  date={schedule.scheduleTime ? new Date(schedule.scheduleTime) : null}
                  onChange={scheduleTime => this.setState({scheduleTime: scheduleTime.getTime()})}
                  invalid={validations.scheduleTime}
                  validationText={validations.scheduleTime}
                />
              </Form.Group>
              <Form.Group>
                <Input
                  type="select"
                  id="scheduled-report-frequency"
                  label="Frequency"
                  value={schedule.interval}
                  onChange={e => this.setState({interval: e.target.value})}>
                  <option value="DAY">Every day</option>
                  <option value="WEEK">Every week</option>
                  <option value="MONTH">Every month</option>
                </Input>
              </Form.Group>
              <Form.Group>
                <Input
                  type="select"
                  id="send-report-as"
                  label="Send report as"
                  helpText="Choose how to recieve your report"
                  value={schedule.format}
                  onChange={e => this.setState({format: e.target.value})}>
                    <option value="PDF">PDF</option>
                    <option value="XLSX">XLSX</option>
                  </Input>
              </Form.Group>
              {timeInterval && (
                <Form.Group>
                  <Input
                    type="select"
                    id="scheduled-report-interval"
                    label="Schedule data from"
                    helpText={!customTimeInterval && `Your scheduled report will contain data from the last ${schedule.parameters.timeInterval} days`}
                    value={customTimeInterval ? 'CUSTOM' : schedule.parameters.timeInterval}
                    onChange={this.handleChangeTimeInterval}>
                    <option value="7">Last week</option>
                    <option value="30">Last month</option>
                    <option value="90">Last quarter</option>
                    <option value="365">Last year</option>
                    <option value="CUSTOM">Custom</option>
                  </Input>
                  {customTimeInterval && (
                    <Input
                      style={{marginTop: '0.5rem'}}
                      type="number"
                      min="1"
                      pattern="[0-9]+"
                      helpText={`Your scheduled report will contain data from the last ${schedule.parameters.timeInterval || '1'} days`}
                      value={schedule.parameters.timeInterval || ''}
                      onChange={this.handleChangeCustomTimeInterval}
                      invalid={validations.timeInterval}
                      validationText={validations.timeInterval}
                    />
                  )}
                </Form.Group>
              )}
              <Form.Group>
                <ChipInput
                  id="scheduled-report-users"
                  label="Users"
                  placeholder="Start typing a username..."
                  helpText="Choose users who will receive this scheduled report"
                  value={schedule.userIds.map(id => usersById[id])}
                  suggestions={users}
                  onChange={users => this.setState({userIds: users.map(user => user.id)})}
                  invalid={validations.users}
                  validationText={validations.users}
                />
              </Form.Group>
              {report && parameters.length > 0 && parameters.map(parameter => (
                <Form.Group key={parameter.name}>
                  <ReportParameterInput
                    report={report}
                    catalog={catalog}
                    parameter={parameter}
                    value={convertParamterValue(parameter, schedule.parameters[parameter.name])}
                    invalid={validations.parameters[parameter.name]}
                    validationText={validations.parameters[parameter.name]}
                    onChange={(parameter, value) => this.setState(state => ({parameters: {...state.parameters, [parameter.name]: serializeParamter(parameter, value)}}))}
                  />
                </Form.Group>
              ))}
            </SideModal.Content>
          )}
        </SideModal>
      </Form>
    )
  }
}

function convertParamterValue(parameter, value) {
  switch (parameter.dataType) {
    case 4:
    case 7:
      return value ? new Date(parseInt(value, 10)) : null;
    default:
      return value;
  }
}

function serializeParamter(parameter, value) {
  return value instanceof Date ? value.getTime() : value;
}

function createSchedule(report = {}) {
  let parameters = {};

  if (hasTimeInterval(report)) {
    parameters.timeInterval = '7';
  }

  return {
    interval: 'DAY',
    parameters,
    scheduleTime: null,
    userIds: [],
    format: 'PDF'
  };
}

function getRelevantParameters(report) {
  if (!report) {
    return [];
  }

  return report.parameters.filter(param => param.name !== 'end_date' && param.name !== 'start_date');
}

function hasTimeInterval(report) {
  const keys = report && report.parameters ? report.parameters.map(parameter => parameter.name) : [];
  return keys.indexOf('start_date') >= 0 && keys.indexOf('end_date') >= 0;
}

function getValidationErrors(report, schedule, {disabled = false} = {}) {
  if (!disabled && !report) {
    return {
      report: 'A report is required.'
    };
  }

  return {
    scheduleTime: disabled || !!schedule.scheduleTime ? null : 'A start date for the schedule is required.',
    users: disabled || schedule.userIds.length > 0 ? null : 'Choose which users should receive this report.',
    timeInterval: disabled || !hasTimeInterval(report) || !isNaN(parseInt(schedule.parameters.timeInterval, 10)) ? null : 'A time interval is required.',
    parameters: disabled ? {} : getRelevantParameters(report).reduce((map, parameter) => {
      const value = schedule.parameters[parameter.name];
      map[parameter.name] = value ? null : 'This parameter is required.';
      return map;
    }, {})
  };
}

function hasValidationErrors(validations) {
  return Object.keys(validations).reduce((result, key) => {
    if (key === 'parameters') {
      return result || hasValidationErrors(validations[key]);
    }

    return result || validations[key] != null;
  }, false);
}

export default withUser(ScheduledReportFormModal);